// Componente card riutilizzabile per ogni integrazione nella lista
import { CheckCircle, XCircle, Settings, Loader2, Wifi, WifiOff } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'

export interface IntegrationStatus {
  name: string
  displayName: string
  description: string
  category: 'erp' | 'hr' | 'messaging' | 'calendar' | 'billing' | 'iot' | 'signing' | 'notifications'
  configured: boolean
  enabled: boolean
  testResult?: { ok: boolean; message: string; latencyMs?: number }
  updatedAt?: string
}

interface IntegrationCardProps {
  integration: IntegrationStatus
  onConfigure: (name: string) => void
  onToggle: (name: string, enabled: boolean) => void
  onTest: (name: string) => void
  isTesting?: boolean
  isToggling?: boolean
}

const categoryColors: Record<IntegrationStatus['category'], string> = {
  erp: 'var(--fc-primary)',
  hr: 'var(--fc-accent)',
  messaging: 'var(--fc-success)',
  calendar: 'var(--fc-info)',
  billing: 'var(--fc-warning)',
  iot: 'var(--fc-primary)',
  signing: 'var(--fc-danger)',
  notifications: 'var(--fc-accent)',
}

const categoryLabels: Record<IntegrationStatus['category'], string> = {
  erp: 'ERP',
  hr: 'HR',
  messaging: 'Messaggistica',
  calendar: 'Calendario',
  billing: 'Fatturazione',
  iot: 'IoT',
  signing: 'Firma',
  notifications: 'Notifiche',
}

export function IntegrationCard({
  integration,
  onConfigure,
  onToggle,
  onTest,
  isTesting = false,
  isToggling = false,
}: IntegrationCardProps) {
  const { name, displayName, description, category, configured, enabled, testResult } = integration

  const statusColor = !enabled
    ? 'default'
    : !configured
    ? 'warning'
    : testResult?.ok === true
    ? 'success'
    : testResult?.ok === false
    ? 'danger'
    : 'primary'

  const statusLabel = !enabled
    ? 'Disabilitata'
    : !configured
    ? 'Non configurata'
    : testResult?.ok === true
    ? 'Connessa'
    : testResult?.ok === false
    ? 'Errore'
    : 'Configurata'

  return (
    <Card>
      <div className="flex items-start gap-4">
        {/* Icona categoria */}
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `color-mix(in srgb, ${categoryColors[category]} 12%, transparent)` }}
        >
          {enabled && configured ? (
            <Wifi className="h-5 w-5" style={{ color: categoryColors[category] }} aria-hidden="true" />
          ) : (
            <WifiOff className="h-5 w-5 text-[var(--fc-foreground-muted)]" aria-hidden="true" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header: nome + badge categoria + status */}
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-[var(--fc-foreground)]">{displayName}</h3>
            <Badge variant="default">{categoryLabels[category]}</Badge>
            <Badge variant={statusColor} dot>{statusLabel}</Badge>
          </div>

          {/* Descrizione */}
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">{description}</p>

          {/* Nota: credenziali solo da env vars */}
          {!configured && (
            <p className="mt-1 text-xs text-[var(--fc-warning)] font-medium">
              Credenziali mancanti — configurare le variabili d'ambiente nel server
            </p>
          )}

          {/* Latenza */}
          {testResult?.ok && testResult.latencyMs !== undefined && (
            <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
              Latenza: {testResult.latencyMs}ms
            </p>
          )}

          {/* Messaggio errore */}
          {testResult?.ok === false && (
            <p className="mt-1 text-xs text-[var(--fc-danger)] truncate" title={testResult.message}>
              {testResult.message}
            </p>
          )}

          {/* Azioni */}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Settings className="h-3 w-3" />}
              onClick={() => onConfigure(name)}
            >
              Configura
            </Button>

            <Button
              variant="secondary"
              size="sm"
              leftIcon={
                isTesting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Wifi className="h-3 w-3" />
                )
              }
              onClick={() => onTest(name)}
              disabled={!configured || isTesting}
            >
              Test
            </Button>

            <Button
              variant={enabled ? 'danger' : 'success'}
              size="sm"
              leftIcon={
                isToggling ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : enabled ? (
                  <XCircle className="h-3 w-3" />
                ) : (
                  <CheckCircle className="h-3 w-3" />
                )
              }
              onClick={() => onToggle(name, !enabled)}
              isLoading={isToggling}
            >
              {enabled ? 'Disabilita' : 'Abilita'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}
